import wind from '../../images/onboarding/wind.png';
import pasoRapido from '../../images/onboarding/paso-rapido.png';
import dgii from '../../images/onboarding/dgii.png';
import caasd from '../../images/onboarding/caasd.png';
import edeeste from '../../images/onboarding/edeeste.png';
import edesur from '../../images/onboarding/edesur.png';
import edenorte from '../../images/onboarding/edenorte.png';
import moun from '../../images/onboarding/moun.svg';
import natcom from '../../images/onboarding/natcom.svg';
import digicel from '../../images/onboarding/digicel.svg';
import viva from '../../images/onboarding/viva.svg';
import altice from '../../images/onboarding/altice.svg';
import claro from '../../images/onboarding/claro.svg';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Brand, OnboardingCard, OnboardingSlide } from '@recargas-dominicanas/core/components';
import { useBusiness } from '@recargas-dominicanas/core/store';
import { useLocalStorage, useShowBuyBalancePage } from '@recargas-dominicanas/core/hooks';
import { remoteConfig } from '../../firebase';
import { style } from './onboarding.module.css';

export function Onboarding() {

  const businessStore = useBusiness();
  const navigate = useNavigate();
  const localStorage = useLocalStorage();
  const showBuyBalancePage = useShowBuyBalancePage(remoteConfig);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    businessStore.fetch();
    const intervalId = setInterval(() => businessStore.fetch(), 2000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {

    if (!emblaApi) return;

    const api = emblaApi;

    function updateSelectedIndex() {
      setSelectedIndex(api.selectedScrollSnap());
    }

    updateSelectedIndex();
    api.on('select', updateSelectedIndex);
    api.on('reInit', updateSelectedIndex);

    return () => {
      api.off('select', updateSelectedIndex);
      api.off('reInit', updateSelectedIndex);
    };
  }, [emblaApi]);

  const benefits = [
    {
      icon: 'payments',
      title: 'Gana comisiones por cada venta',
      description: 'Cada recarga y servicio suma ingresos para tu negocio.'
    },
    {
      icon: 'trending_up',
      title: 'Aumenta las ventas de tu negocio',
      description: 'Aprovecha cada visita para ofrecer más productos.'
    },
    {
      icon: 'groups',
      title: 'Atrae más clientes a tu negocio',
      description: 'Ofréceles servicios que necesitan todos los días.'
    }
  ];

  const brands = [
    { name: 'Claro', image: claro },
    { name: 'Altice', image: altice },
    { name: 'Viva', image: viva },
    { name: 'Digicel', image: digicel },
    { name: 'Natcom', image: natcom },
    { name: 'Moun', image: moun },
    { name: 'Edenorte', image: edenorte },
    { name: 'Edesur', image: edesur },
    { name: 'Edeeste', image: edeeste },
    { name: 'CAASD', image: caasd },
    { name: 'DGII', image: dgii },
    { name: 'Paso Rápido', image: pasoRapido },
    { name: 'Wind', image: wind }
  ];

  const commissions = [
    {
      icon: 'phone_android',
      title: '5.5% de comisión en recargas telefónicas',
      description: 'Gana por cada venta completada.'
    },
    {
      icon: 'receipt_long',
      title: 'Comisiones por facturas y servicios',
      description: 'El monto depende del producto que cobres.'
    },
    {
      icon: 'signal_cellular_alt',
      title: 'Mientras más vendas, más altas serán tus comisiones',
      description: 'Tu porcentaje de comisión aumenta con el volumen de ventas.'
    }
  ];

  const activationSteps = [
    {
      title: 'Deposita o transfiere',
      description: 'Usa una de las cuentas bancarias oficiales que verás a continuación.'
    },
    {
      title: 'Coloca tu ID de negocio',
      description: `Usa el ID ${businessStore.business.id} como referencia o concepto del pago.`
    },
    {
      title: 'Envía el comprobante',
      description: 'Toma una foto del recibo y compártela por WhatsApp.'
    }
  ];

  function handleNext() {
    if (selectedIndex === 3) {
      localStorage.setOnboardingCompleted(true);
      navigate(showBuyBalancePage ? '/como-comprar-balance' : '/contactar-ventas');
    } else {
      emblaApi?.scrollNext(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }
  }

  if (!businessStore.business?.customer ||
      businessStore.business.customer.status !== 'new' ||
      localStorage.onboardingCompleted
  ) {
    return <Navigate to='/' replace={true}/>;
  }

  return (
    <main className={style.container}>
      <div className={style.content}>
        <header className={style.header}>
          <Brand style={style.brand}/>
        </header>
        <div className={style.viewport} ref={emblaRef}>
          <div className={style.slides}>
            <OnboardingSlide
              style={style.welcomeSlide}
              index={0}
              selectedIndex={selectedIndex}
              icon='celebration'
              title='¡Bienvenido a Recargas Dominicanas!'
              description='Comienza a vender recargas e incrementa los ingresos de tu negocio.'>
              <div className={style.cards}>
                {benefits.map(benefit => (
                  <OnboardingCard
                    key={benefit.title}
                    symbol={benefit.icon}
                    title={benefit.title}
                    description={benefit.description}/>
                ))}
              </div>
            </OnboardingSlide>
            <OnboardingSlide
              style={style.productSlide}
              index={1}
              selectedIndex={selectedIndex}
              icon='category'
              title='Más servicios para tus clientes'
              description='Ofrece recargas, paqueticos, pines y pago de facturas desde un solo lugar.'>
              <div className={style.bubbles}>
                {brands.map(brand => (
                  <img
                    key={brand.name}
                    className={style.bubble}
                    src={brand.image}
                    alt={brand.name}/>
                ))}
              </div>
            </OnboardingSlide>
            <OnboardingSlide
              style={style.commissionsSlide}
              index={2}
              selectedIndex={selectedIndex}
              icon='trending_up'
              title='Gana con cada transacción'
              description='Convierte los servicios de todos los días en ingresos adicionales.'>
              <div className={style.cards}>
                {commissions.map(commission => (
                  <OnboardingCard
                    key={commission.title}
                    symbol={commission.icon}
                    title={commission.title}
                    description={commission.description}/>
                ))}
              </div>
            </OnboardingSlide>
            <OnboardingSlide
              style={style.activateSlide}
              index={3}
              selectedIndex={selectedIndex}
              icon='account_balance'
              title='Activa tu cuenta en tres pasos'
              description='Compra balance y envía tu comprobante para comenzar a vender.'>
              <div className={style.cards}>
                {activationSteps.map((step, index) => (
                  <OnboardingCard
                    key={step.title}
                    symbol={index + 1}
                    title={step.title}
                    description={step.description}/>
                ))}
              </div>
            </OnboardingSlide>
          </div>
        </div>
      </div>
      <footer className={style.footer}>
        <nav className={style.dots} aria-label='Progreso del recorrido'>
          {[0, 1, 2, 3].map(index => (
            <button
              key={index}
              className={clsx(style.dot, selectedIndex === index && style.selectedDot)}
              type='button'
              aria-label={`Ir al paso ${index + 1} de 4`}
              aria-current={selectedIndex === index ? 'step' : undefined}
              onClick={() => emblaApi?.scrollTo(index, window.matchMedia('(prefers-reduced-motion: reduce)').matches)}/>
          ))}
        </nav>
        <div className={style.actions}>
          <button
            className={style.previousButton}
            type='button'
            disabled={selectedIndex === 0}
            onClick={() => emblaApi?.scrollPrev(window.matchMedia('(prefers-reduced-motion: reduce)').matches)}>
            Atrás
          </button>
          <button
            className={style.nextButton}
            type='button'
            onClick={handleNext}>
            {selectedIndex === 3 ? 'Comprar balance' : 'Siguiente'}
          </button>
        </div>
      </footer>
    </main>
  );
}
